import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Home, ChevronRight, Calendar } from "lucide-react";
import { RichTextContent } from "../components/RichTextContent";

interface PolicyDetail {
  id: number;
  information_title: string;
  information_content: string;
  article_type: string;
  tag: string;
  release_time: string;
  create_time: string;
}

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [policy, setPolicy] = useState<PolicyDetail | null>(null);
  const [relatedPolicies, setRelatedPolicies] = useState<PolicyDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchPolicy(id);
      fetchRelatedPolicies();
    }
  }, [id]);

  const fetchPolicy = async (articleId: string) => {
    try {
      const resp = await fetch(`/api/public/information/${articleId}`);
      const data = await resp.json();
      if (data.ok) {
        setPolicy(data.data);
      }
    } catch (err) {
      console.error('获取文章详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPolicies = async () => {
    try {
      const resp = await fetch('/api/public/information/policy');
      const data = await resp.json();
      if (data.ok) {
        setRelatedPolicies(data.data || []);
      }
    } catch (err) {
      console.error('获取相关文章失败:', err);
    }
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>文章不存在</p>
          <Link to="/policies" style={{ color: '#003c8c', marginTop: '16px', display: 'inline-block' }}>
            返回政策法规列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: '48px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{ width: '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {policy.tag && (
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '20px',
              fontSize: '14px',
              color: 'white',
              marginBottom: '16px'
            }}>
              {policy.tag}
            </span>
          )}
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 700, 
            color: 'white',
            lineHeight: 1.4,
            maxWidth: '900px'
          }}>
            {policy.information_title}
          </h1>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: '80%', margin: '0 auto', padding: '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: '16px', height: '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <Link to="/policies" style={{ color: '#6b7280', textDecoration: 'none' }}>政策法规</Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <span style={{ color: '#003c8c', fontWeight: 500 }}>文章详情</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: '80%', margin: '0 auto', padding: '40px 0' }}>
        <div style={{ display: 'flex', gap: '40px' }}>
          {/* 文章内容 */}
          <div style={{ flex: 1 }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              {/* 文章元信息 */}
              <div style={{
                padding: '24px 40px',
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: '#fafbfc'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '24px', fontSize: '14px', color: '#6b7280' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Calendar style={{ width: '16px', height: '16px', color: '#003c8c' }} />
                    发布时间：{formatDateTime(policy.release_time)}
                  </span>
                </div>
              </div>

              {/* 文章正文 */}
              <div style={{ padding: '40px' }}>
                <RichTextContent
                  html={policy.information_content}
                  style={{ fontSize: "16px", lineHeight: 1.9, color: "#374151" }}
                />
              </div>
            </div>
          </div>

          {/* 侧边栏 */}
          <div style={{ width: '280px', flexShrink: 0 }}>
            {/* 相关推荐 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>相关推荐</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {relatedPolicies
                  .filter(p => p.id !== policy.id)
                  .slice(0, 4)
                  .map(p => (
                    <Link
                      key={p.id}
                      to={`/policy/${p.id}`}
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        color: '#4b5563',
                        textDecoration: 'none',
                        lineHeight: 1.6,
                        paddingBottom: '16px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      {p.information_title.length > 30 ? p.information_title.slice(0, 30) + '...' : p.information_title}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
